"use client"
import { motion } from "framer-motion"
import type { ReactNode } from "react"

interface SocialLinksProps {
  className?: string
  linkedin?: boolean
  bsky?: boolean
  reddit?: boolean
}

export function SocialLinks({ className, linkedin, bsky, reddit }: SocialLinksProps) {
  const enabled = { linkedin, bsky, reddit }

  const socialLinks: Array<{ key: keyof typeof enabled; name: string; url: string; icon: ReactNode }> = [
    {
      key: "linkedin",
      name: "LinkedIn",
      url: "https://www.linkedin.com/company/thehiitpit/",
      icon: (
        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-[#0A66C2]">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
    },
    {
      key: "bsky",
      name: "Bluesky",
      url: "https://bsky.app/profile/thehiitpit.com",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -3.268 64 68.414" className="h-5 w-5 fill-[#0085FF]">
          <path d="M13.873 3.805C21.21 9.332 29.103 20.537 32 26.55v15.882c0-.338-.13.044-.41.867-1.512 4.456-7.418 21.847-20.923 7.944-7.111-7.32-3.819-14.64 9.125-16.85-7.405 1.264-15.73-.825-18.014-9.015C1.12 23.022 0 8.51 0 6.55 0-3.268 8.579-.182 13.873 3.805zm36.254 0C42.79 9.332 34.897 20.537 32 26.55v15.882c0-.338.13.044.41.867 1.512 4.456 7.418 21.847 20.923 7.944 7.111-7.32 3.819-14.64-9.125-16.85 7.405 1.264 15.73-.825 18.014-9.015C62.88 23.022 64 8.51 64 6.55c0-9.818-8.578-6.732-13.873-2.745z" />
        </svg>
      ),
    },
    {
      key: "reddit",
      name: "Reddit",
      url: "https://www.reddit.com/r/thehiitpit/",
      icon: (
        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-[#FF4500]">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
        </svg>
      ),
    },
  ]

  const selectedSocialLinks = socialLinks.filter((link) => enabled[link.key])

  if (selectedSocialLinks.length === 0) {
    return null
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {selectedSocialLinks.map((link) => (
        <motion.a
          key={link.name}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center rounded-full bg-white dark:bg-gray-800 p-2 shadow-sm hover:shadow-md transition-all duration-200"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          aria-label={`Follow us on ${link.name}`}
          title={`Follow us on ${link.name}`}
          data-analytics-category="SOCIAL_ENGAGEMENT"
          data-analytics-action="CLICK"
          data-analytics-name={`SOCIAL_${link.name.toUpperCase()}`}
        >
          {link.icon}
        </motion.a>
      ))}
    </div>
  )
}
